import React from 'react';
import { Link } from 'react-router-dom';
import { Dumbbell, Shield, ArrowLeft } from 'lucide-react';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white font-sans">
      {/* Nav */}
      <nav className="sticky top-0 z-50 border-b border-white/5 bg-[#0a0a0a]/90 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg">
              <Dumbbell size={15} className="text-white" />
            </div>
            <span className="text-lg font-black tracking-tight">
              Fitxero<span className="text-emerald-400">.</span>
            </span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-sm font-semibold text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft size={15} /> Back to Home
          </Link>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-6 py-20">
        {/* Header */}
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
            <Shield size={20} className="text-emerald-400" />
          </div>
          <span className="text-xs font-bold text-emerald-400 tracking-widest uppercase">
            Legal Document
          </span>
        </div>
        <h1 className="text-4xl font-black tracking-tight mb-3">
          Privacy Policy & Terms of Service
        </h1>
        <p className="text-gray-400 mb-2 text-sm">
          Last updated: April 14, 2026 · Effective immediately
        </p>
        <p className="text-gray-400 mb-12 text-sm">
          This document governs your use of Fitxero — a gym management SaaS platform operated by
          Fitxero Inc. By using our platform, you agree to these terms.
        </p>

        <div className="space-y-10 text-gray-300 leading-relaxed">
          {[
            {
              title: '1. Information We Collect',
              content: `When you register for Fitxero, we collect the information you provide directly — including your name, email address, gym name, phone number, and city. We also collect information about how you use our services, including log data (IP address, browser type, pages visited) and usage analytics to improve the product. We do NOT collect payment card information directly — any future payment processing will be handled by compliant third-party providers.`,
            },
            {
              title: '2. How We Use Your Information',
              content: `We use your information to: (a) provide and maintain the Fitxero gym management service; (b) authenticate your identity and manage your account; (c) send service-related emails such as trial expiry notices and product updates; (d) respond to your support requests; (e) improve our product based on aggregated usage patterns. We will NEVER sell your data to third parties.`,
            },
            {
              title: '3. Data Storage & Security',
              content: `Your data is stored on secure, encrypted cloud infrastructure. We implement industry-standard security measures including HTTPS encryption, hashed passwords (bcrypt), and JWT-based authentication. While no system is 100% immune to breaches, we take all reasonable precautions to protect your data. Backups are performed regularly to prevent data loss.`,
            },
            {
              title: '4. Member Data Responsibility',
              content: `As a gym owner using Fitxero, you are responsible for the personal data of your gym members that you upload or enter into the platform. You agree to: (a) only collect data from your members with their consent; (b) use member data solely for gym management purposes; (c) honour member requests to delete their data. Fitxero acts as a data processor on your behalf and follows your instructions regarding member data.`,
            },
            {
              title: '5. Your 7-Day Free Trial',
              content: `New accounts begin a 7-day free trial with full platform access. No credit card is required during the trial. At the end of the trial, your account will be restricted until you subscribe to a paid plan. Your data is retained for 30 days after trial expiry to allow you to re-activate. After 30 days of inactivity on an expired account, data may be permanently deleted with prior email notice.`,
            },
            {
              title: '6. Subscription & Cancellation',
              content: `Paid subscriptions are billed monthly or annually as selected. You may cancel your subscription at any time through your account settings. Cancellation takes effect at the end of the current billing period. We do not provide pro-rated refunds for partial months. If you believe you were charged in error, contact support@fitxero.com within 14 days of the charge.`,
            },
            {
              title: '7. Third-Party Services',
              content: `Fitxero uses trusted third-party services to operate: hosting providers, email delivery services, and analytics tools. These services have their own privacy policies and are contractually bound to process your data securely and only for the purposes we specify.`,
            },
            {
              title: '8. Data Deletion',
              content: `You may request complete deletion of your gym account and all associated data by emailing support@fitxero.com. We will process deletion requests within 14 business days. Note that some data may be retained in encrypted backups for up to 30 additional days before being purged from all systems.`,
            },
            {
              title: '9. Changes to This Policy',
              content: `We may update this Privacy Policy and Terms of Service from time to time. We will notify all account holders via email at least 7 days before any material changes take effect. Continued use of the platform after changes take effect constitutes acceptance of the new terms.`,
            },
            {
              title: '10. Contact Us',
              content: `If you have questions about this Privacy Policy or how we handle your data, please reach out: Email: support@fitxero.com. We aim to respond to all inquiries within 2 business days.`,
            },
          ].map(({ title, content }) => (
            <section key={title}>
              <h2 className="text-lg font-bold text-white mb-3 pb-2 border-b border-white/5">
                {title}
              </h2>
              <p className="text-gray-400 text-sm leading-7">{content}</p>
            </section>
          ))}
        </div>

        <div className="mt-16 p-6 rounded-2xl border border-emerald-500/20 bg-emerald-500/5">
          <p className="text-sm text-emerald-400 font-semibold mb-1">Questions?</p>
          <p className="text-sm text-gray-400">
            Contact us at{' '}
            <a
              href="mailto:support@fitxero.com"
              className="text-emerald-400 hover:underline font-semibold"
            >
              support@fitxero.com
            </a>{' '}
            and we'll respond within 2 business days.
          </p>
        </div>

        <p className="text-center text-xs text-gray-600 mt-12">
          © 2026 Fitxero Inc. All rights reserved.
        </p>
      </div>
    </div>
  );
}
